import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, FileWarning, Lock, BadgeCheck, CheckCircle2 } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import CTAStrip from "@/components/site/CTAStrip";
import RelatedLinks from "@/components/site/RelatedLinks";
import { useSEO } from "@/lib/seo";
import { linkifyGoAndTrack } from "@/lib/goAndTrack";

const IMG = "/images/ll100-scan.jpg";

export default function HighValueShipments() {
  useSEO({
    title: "Smart Labels for High-Value Shipments | Package Tracking Device",
    description: "Disposable smart labels and package tracking devices for high-value shipments. Independent journey evidence, exception alerts, and chain-of-custody clarity.",
    canonical: "/smart-labels-for-high-value-shipments",
  });

  return (
    <div data-testid="page-high-value">
      <PageHero
        eyebrow="Use case"
        title="Smart Labels for High-Value Shipments"
        subtitle="Independent visibility on shipments where loss, delay, or damage carries serious commercial weight — deployed without locking your team into hardware recovery."
      >
        <Link to="/request-a-trial" className="btn-primary" data-testid="page-cta-trial">
          Speak to an expert <ArrowRight className="h-4 w-4" />
        </Link>
      </PageHero>

      <section className="section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="border border-line bg-white">
              <div className="aspect-[5/4] overflow-hidden">
                <img src={IMG} alt="Scanning a disposable smart label on a high-value shipment for independent journey evidence" className="h-full w-full object-cover" />
              </div>
            </div>
          </div>
          <div className="lg:col-span-6">
            <p className="eyebrow mb-4">Why high-value teams use disposable labels</p>
            <h2 className="h2">Evidence-grade visibility, no recovery loop.</h2>
            <p className="lead mt-5">
              High-value shipments demand independent, timestamped data — but
              not the operational complexity of recovering hardware from
              customer sites or partner depots.
            </p>
            <ul className="mt-8 space-y-3.5">
              {[
                "Independent journey evidence for claims and dispute resolution.",
                "Granular dwell and route data for sensitive lanes.",
                "Exception alerts via GoAndTrack — handled by your operations team.",
                "Single-use means there's no expensive tracker stuck at a customer site.",
              ].map((b) => (
                <li key={b} className="flex items-start gap-3 text-[15px] text-ink">
                  <CheckCircle2 className="h-4 w-4 mt-1 text-brand shrink-0" />
                  {linkifyGoAndTrack(b)}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="section bg-ink text-white">
        <div className="container-x">
          <div className="max-w-2xl mb-12">
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-white/60 mb-4">Operational fit</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight leading-[1.1]">
              When disposable beats reusable for high-value lanes.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
            {[
              { icon: ShieldCheck, k: "Direct-to-customer high-value", v: "When the shipment ends at a third party and recovery of a reusable tracker is unrealistic or commercially awkward." },
              { icon: FileWarning, k: "Sensitive single-trip cargo", v: "Where you need clean, defensible data for one specific journey, not a perpetual fleet of devices." },
              { icon: Lock, k: "Tamper and exception evidence", v: "Disposable labels deliver timestamped journey data that supports investigations without reverse-logistics complexity." },
              { icon: BadgeCheck, k: "Insurance and claims posture", v: "Independent shipment data improves your position on disputes, missed SLAs, and damage claims." },
            ].map((b) => (
              <div key={b.k} className="bg-ink p-8">
                <b.icon className="h-5 w-5 text-white/80" />
                <h3 className="font-heading text-xl font-semibold mt-4">{b.k}</h3>
                <p className="mt-2 text-sm text-white/70">{b.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <RelatedLinks
        title="Related reading for high-value logistics"
        links={[
          { to: "/smart-labels-for-logistics", label: "Smart Labels for Logistics — cross-lane visibility" },
          { to: "/disposable-gps-tracker", label: "Disposable GPS Tracker — single-use tracking devices" },
          { to: "/gps-tracker-for-packages", label: "GPS Tracker for Packages — package-level tracking" },
          { to: "/disposable-vs-reusable-trackers", label: "Disposable vs Reusable Trackers — full comparison" },
        ]}
      />

      <CTAStrip
        title="Protect high-value lanes with single-use visibility."
        body="Talk to us about the shipments where independent journey data matters most."
      />
    </div>
  );
}
